import type { Connection, Entity, LibraryGroup, StoryNode } from '@dispositio/core';
import type { ChangeSet, HistoryEntry, HistoryStep } from './dto.js';

// ---------------------------------------------------------------------------
// Verlauf (Memento): Ein Schritt hält von jedem Datensatz, den eine Änderung berührt hat, den Stand vorher und nachher. Gebildet wird
// er aus der Geschichte vor der Änderung und der Antwort des Servers (ChangeSet) – damit zählen auch Nebenwirkungen (automatisch
// angelegte Kopien, mitgelöschte Verbindungen, umgeschriebene Verweise) zum Schritt.
// ---------------------------------------------------------------------------

/** Was ein Schritt kennt: die Listen einer Geschichte */
export interface StorySlice {
  nodes: readonly StoryNode[];
  entities: readonly Entity[];
  groups: readonly LibraryGroup[];
  connections: readonly Connection[];
}

export const emptyStep = (): HistoryStep => ({ nodes: [], entities: [], groups: [], connections: [] });

/** Gleichheit zweier Datensätze (Schlüssel sortiert, undefinierte Werte zählen nicht) */
const canonical = (value: unknown): string =>
  JSON.stringify(value, (_k, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== undefined).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) : v,
  );
export const sameRecord = (a: unknown, b: unknown): boolean => (a === null || b === null ? a === b : canonical(a) === canonical(b));

function entriesOf<T extends { id: string }>(before: readonly T[], changed: readonly T[], removed: readonly string[]): HistoryEntry<T>[] {
  const was = new Map(before.map((r) => [r.id, r]));
  const out: HistoryEntry<T>[] = [];
  for (const r of changed) out.push({ id: r.id, from: was.get(r.id) ?? null, to: r });
  for (const id of removed) if (was.has(id) && !changed.some((r) => r.id === id)) out.push({ id, from: was.get(id)!, to: null });
  // was sich nicht wirklich geändert hat, gehört nicht zum Schritt
  return out.filter((e) => !sameRecord(e.from, e.to));
}

/** Schritt einer Änderung: `before` = Geschichte davor (vor der sofort sichtbaren Vorschau), `cs` = Antwort des Servers. */
export function stepOf(before: StorySlice, cs: ChangeSet): HistoryStep {
  return {
    nodes: entriesOf(before.nodes, cs.nodes, cs.removed.nodes),
    entities: entriesOf(before.entities, cs.entities, cs.removed.entities),
    groups: entriesOf(before.groups, cs.groups, cs.removed.groups),
    connections: entriesOf(before.connections, cs.connections, cs.removed.connections),
  };
}

export const isEmptyStep = (s: HistoryStep): boolean => s.nodes.length + s.entities.length + s.groups.length + s.connections.length === 0;

const flip = <T>(list: HistoryEntry<T>[]): HistoryEntry<T>[] => list.map((e) => ({ id: e.id, from: e.to, to: e.from }));

/** Der umgekehrte Schritt (Rückgängig) */
export const invertStep = (s: HistoryStep): HistoryStep => ({ nodes: flip(s.nodes), entities: flip(s.entities), groups: flip(s.groups), connections: flip(s.connections) });

function joined<T>(a: HistoryEntry<T>[], b: HistoryEntry<T>[]): HistoryEntry<T>[] {
  const out = new Map(a.map((e) => [e.id, { ...e }]));
  for (const e of b) {
    const first = out.get(e.id);
    out.set(e.id, first ? { id: e.id, from: first.from, to: e.to } : { ...e });
  }
  return [...out.values()].filter((e) => !sameRecord(e.from, e.to));
}

/** Zwei aufeinanderfolgende Schritte als einer: vorher wie im ersten, nachher wie im zweiten */
export const mergeSteps = (a: HistoryStep, b: HistoryStep): HistoryStep => ({
  nodes: joined(a.nodes, b.nodes),
  entities: joined(a.entities, b.entities),
  groups: joined(a.groups, b.groups),
  connections: joined(a.connections, b.connections),
});
